import React, { memo, useCallback } from 'react';
import { Box, Card, Group, Stack } from '@mantine/core';
import { useRouter } from 'next/router';
import { formatDistanceToNow } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import type { Translate } from 'next-translate';
import { match } from 'ts-pattern';

import { UnstyledNextLink } from '~/components/shared/UnstyledNextLink/UnstyledNextLink';
import { formatDollars } from '~/domains/common/utils/formatters';
import { Avatar } from '~/domains/design-system/Avatar';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import type { OfferFeedItem } from '~/services/marketplace/marketplace-service.schemas';
import PickControlIndicator from '~/domains/contest/domains/gamecenter/domains/common/components/PickControlIndicator/PickControlIndicator';

import type { OfferFeedKind } from '../../utils/resolveOfferFeedKind';
import { resolveOfferFeedTerms } from '../../utils/resolveOfferFeedKind';
import useInviteCardStyles from '~/domains/common/components/AlertCard/alertCard.styles';

import { ACTOR_AVATAR_SIZE, ENTRY_AVATAR_SIZE } from './OfferFeedCard.styles';

const resolveSharePercent = (item: OfferFeedItem, shareBps: number) =>
  item.sharePercent ?? Math.round(shareBps / 100);

const resolveHeadline = (item: OfferFeedItem, kind: OfferFeedKind, t: Translate) =>
  match(kind)
    .with('offer', () => t('common.offerFeed.madeAnOffer', { handle: item.actor.handle }))
    .with('counter', () => t('common.offerFeed.counteredYourOffer', { handle: item.actor.handle }))
    .with('accepted', () => t('common.offerFeed.acceptedYourOffer', { handle: item.actor.handle }))
    .exhaustive();

function OfferFeedCardExpiry({ item, kind }: { item: OfferFeedItem; kind: OfferFeedKind }) {
  const { classes } = useInviteCardStyles();
  const { t } = useTranslation('contest');

  if (kind === 'accepted' || !item.expiresAt) {
    return null;
  }

  const expiry = new Date(item.expiresAt);

  if (Number.isNaN(expiry.getTime())) {
    return null;
  }

  return (
    <Text variant="body-medium" fw={500}>
      <span className={classes.expiryPrefix}>
        {t(
          kind === 'counter' ? 'common.offerFeed.counterExpires' : 'common.offerFeed.offerExpires'
        )}{' '}
      </span>
      <span className={classes.expiryTime}>{formatDistanceToNow(expiry, { addSuffix: true })}</span>
    </Text>
  );
}

function OfferFeedCard({
  item,
  kind,
  href,
  onDecline,
  onDismiss,
  onView,
  isBusy,
}: {
  item: OfferFeedItem;
  kind: OfferFeedKind;
  href: string;
  onDecline: (item: OfferFeedItem) => void;
  onDismiss: (item: OfferFeedItem) => void;
  onView: (item: OfferFeedItem) => Promise<void>;
  isBusy?: boolean;
}) {
  const { classes, theme } = useInviteCardStyles();
  const { t } = useTranslation('contest');
  const router = useRouter();

  const { priceDollars, shareBps } = resolveOfferFeedTerms(item, kind);

  const isAccepted = kind === 'accepted';
  const entryLabel = item.entryLabel || t('common.offerFeed.entryUnknown');

  const handleSecondaryAction = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (isAccepted) {
        onDismiss(item);
        return;
      }

      onDecline(item);
    },
    [isAccepted, item, onDecline, onDismiss]
  );

  const handlePrimaryAction = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      await onView(item);
      router.push(href);
    },
    [href, item, onView, router]
  );

  return (
    <Card className={classes.container} p="md" radius="lg">
      <Stack spacing={theme.other.spacing._12}>
        <UnstyledNextLink href={href} className={classes.contentLink} onClick={handlePrimaryAction}>
          <Group spacing="xs">
            <Avatar
              src={item.actor.avatarUrl ?? undefined}
              handle={item.actor.handle}
              size={ACTOR_AVATAR_SIZE}
            />
            <Text className={classes.inviterName}>{resolveHeadline(item, kind, t)}</Text>
          </Group>

          <Group className={classes.contestSection} spacing="sm">
            <Avatar
              src={item.contest?.imageUrl ?? item.commissioner?.avatarUrl ?? undefined}
              handle={item.contest?.name ?? entryLabel}
              size={ENTRY_AVATAR_SIZE}
            />
            <Box className={classes.contestInfo}>
              <Title variant="headline-medium" className={classes.contestName}>
                {entryLabel}
              </Title>
              <Group className={classes.contestMeta}>
                <Text variant="body-medium" className={classes.metaItem}>
                  <span className={classes.metaLabel}>
                    {isAccepted
                      ? t('common.offerFeed.boughtLabel')
                      : t(
                          kind === 'counter'
                            ? 'common.offerFeed.counterTerms'
                            : 'common.offerFeed.offerTerms'
                        )}
                  </span>{' '}
                  <span className={classes.metaValue}>{formatDollars(priceDollars)}</span>
                </Text>
                <Text className={classes.metaDivider} variant="body-small">
                  •
                </Text>
                <Text variant="body-medium" className={classes.metaItem}>
                  <span className={classes.metaLabel}>{t('common.offerFeed.shareLabel')}</span>{' '}
                  <PickControlIndicator
                    isPickController={!!item.conveysPickControl}
                    tone="strong"
                  />
                  <span className={classes.metaValue}>{resolveSharePercent(item, shareBps)}%</span>
                </Text>
              </Group>
            </Box>
          </Group>
        </UnstyledNextLink>

        <Group grow spacing={theme.other.spacing._8}>
          <Button
            variant="tertiary-fill"
            size="small"
            disabled={isBusy}
            onClick={handleSecondaryAction}
          >
            <Text variant="label-small">
              {isAccepted ? t('common.offerFeed.dismiss') : t('common.offerFeed.decline')}
            </Text>
          </Button>
          <Button
            variant="primary-fill"
            size="small"
            disabled={isBusy}
            onClick={handlePrimaryAction}
          >
            <Text variant="label-small">
              {match(kind)
                .with('offer', () => t('common.offerFeed.viewOffer'))
                .with('counter', () => t('common.offerFeed.viewCounter'))
                .with('accepted', () => t('common.offerFeed.viewMyEntry'))
                .exhaustive()}
            </Text>
          </Button>
        </Group>

        <OfferFeedCardExpiry item={item} kind={kind} />
      </Stack>
    </Card>
  );
}

export default memo(OfferFeedCard);
